// React.
import { useEffect, useMemo, useRef, useState } from 'react';

// Third-parties.
import { Column, usePagination, useSortBy, useTable } from 'react-table';
import TableCSVExport from '../../../component/TableCSVExport';

// Types.
import { Parameter } from '../../../types/instance/Parameter';

// Components.
import ReactTable from '../../ReactTable';

// Helper.
import { getDefaultPageSize } from '../../../helpers/utils';
import { useLocation, useNavigate } from "react-router-dom";
import SearchInput from '../../SearchInput';

export default function ParameterTable (props: {
	error: string | null,
	loading: boolean,
	data: Parameter[]
})
{
	const { error, loading, data } = props;

	const navigate = useNavigate();
	const location = useLocation();
	const query = new URLSearchParams(location.search);
	const [tableFilter, setTableFilter] = useState(query.get("filter") || "");
	const initialPageIndex = Number(query.get("page") || 0);
	const initialPageSize = Number(query.get("pageSize") || getDefaultPageSize());
	const inputRef = useRef<HTMLInputElement>(null);

	useEffect(() => {
		if (inputRef.current) {
			inputRef.current.focus({ preventScroll: true });
		}
	}, []);

	// Table columns.
	const columns: Column[] = useMemo(() =>
	{
		return [
			{
				Header: 'Parameter',
				accessor: 'parameter',
				headerClassName: 'text',
				canSort: true,
				sortDescFirst: false,
				sortType: 'basic'
			},
			{
				Header: 'Value',
				accessor: 'value',
				headerClassName: 'text',
				canSort: true,
				sortDescFirst: false,
				sortType: 'basic'
			}
		]
	}, []);

	const tableData = useMemo (() =>
			data.filter ((row) =>
				row.parameter.toLowerCase ().includes (tableFilter.toLowerCase ()) ||
				row.value?.toLowerCase ().includes (tableFilter.toLowerCase ())),
		[ data, tableFilter ]);

	const tableInstance = useTable (
	{
		columns,
		data: tableData,
		initialState:
		{
			pageIndex: initialPageIndex,
			pageSize: initialPageSize,
		},
		disableSortRemove: true
	}, useSortBy, usePagination);

	const
	{
		getTableProps,
		getTableBodyProps,
		headerGroups,
		prepareRow,
		page,
		setPageSize,
		pageOptions,
		pageCount,
		canPreviousPage,
		previousPage,
		canNextPage,
		nextPage,
		gotoPage,
		state: { pageIndex, pageSize }
	} = tableInstance;

	useEffect(() => {
		const params = new URLSearchParams(location.search);

		if (tableFilter) {
			params.set('filter', tableFilter);
		} else {
			params.delete('filter');
		}

		params.set('page', pageIndex.toString());
		params.set('pageSize', pageSize.toString());

		navigate({ search: params.toString() }, { replace: true });
	}, [tableFilter, pageIndex, pageSize]);

	useEffect(() => {
		if (tableFilter) {
			gotoPage(0);
		}
	}, [tableFilter]);

	function clearTableFilter ()
	{
		setTableFilter ('');
	}


	// The bracket must be present and on the same line as the return.
	return (
		<div className="card">
			<div className="card-header">
				<i className="fal fa-scroll fa-fw" aria-hidden="true"/>
				Parameters
				<span className="badge bg-info"
					  data-tooltip-id="global-tooltip" data-tooltip-content="Total change count">{data.length.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")}</span>
				<i className="collapse-toggle" role="button" data-bs-toggle="collapse"
				   data-bs-target="#collapse-usage" aria-expanded="false" aria-controls="collapse-usage"/>
			</div>
			<div id="collapse-usage" className="card-body collapse show">
				<div className="row row-cols-1 row-cols-md-2 table-search">
<div className="col col-md-10">
                        <TableCSVExport
							tableInstance={tableInstance}
							filename="Parameters"
						/>
					</div>
					<div className="col col-md-2">
						<SearchInput
							ref={inputRef}
							value={tableFilter}
							onChange={(e) => setTableFilter(e.target.value)}
							onClear={clearTableFilter}
						/>
					</div>
				</div>
				<ReactTable
					getTableProps={getTableProps}
					getTableBodyProps={getTableBodyProps}
					headerGroups={headerGroups}
					prepareRow={prepareRow}
					page={page}
					canPreviousPage={canPreviousPage}
					canNextPage={canNextPage}
					pageOptions={pageOptions}
					pageCount={pageCount}
					gotoPage={gotoPage}
					nextPage={nextPage}
					previousPage={previousPage}
					setPageSize={setPageSize}
					pageIndex={pageIndex}
					pageSize={pageSize}
					dataLength={data.length}
					error={error}
					loading={loading}
				/>
			</div>
		</div>
	)
}
